import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Flame, Sparkles, Footprints, Coffee, Activity, ClipboardList, Trophy, Brain, ChevronRight, BookOpen, Gift, Swords, Zap, Smile, Star, Award, Target, HeartPulse } from "lucide-react";
import PhoneFrame from "../components/PhoneFrame.jsx";
import BottomNav from "../components/BottomNav.jsx";
import StepRing from "../components/StepRing.jsx";
import PostureGauge from "../components/PostureGauge.jsx";
import { Wordmark } from "../components/Brand.jsx";
import NotificationBell from "../components/NotificationBell.jsx";
import AnnualGoalCard from "../components/AnnualGoalCard.jsx";
import FireStreak from "../components/FireStreak.jsx";
import SpinWheel from "../components/SpinWheel.jsx";
import DailyMissionCard from "../components/DailyMissionCard.jsx";
import QuickActionCard, { StreakChip } from "../components/QuickActionCard.jsx";
import MiniSpinWheel from "../components/MiniSpinWheel.jsx";
import PostureWellnessHero from "../components/PostureWellnessHero.jsx";
import CoachNudgeCard from "../components/CoachNudgeCard.jsx";
import api from "../lib/api.js";
import { useAuth } from "../lib/auth.jsx";
import { goalConfetti, chime } from "../lib/celebrate.js";

const MOOD_LABELS = ["—", "Drained", "Low", "Meh", "Okay", "Steady", "Lifted", "Good", "Energized", "Buzzing", "Euphoric"];

export default function HomeDash() {
  const { user, logout, refreshUser } = useAuth();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [spinOpen, setSpinOpen] = useState(false);
  const [spinClaimed, setSpinClaimed] = useState(false);
  const celebratedRef = useRef(false);

  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data)).catch(() => {});
    api.get("/daily/spin/status").then((r) => setSpinClaimed(r.data.claimed)).catch(() => {});
  }, []);

  // Celebrate when daily goal is hit (once per session)
  useEffect(() => {
    if (!d) return;
    const hit = d.total_posture_steps >= d.daily_goal;
    if (hit && !celebratedRef.current) {
      celebratedRef.current = true;
      const sessionKey = `ps-goal-celebrated-${new Date().toISOString().slice(0,10)}`;
      if (!sessionStorage.getItem(sessionKey)) {
        sessionStorage.setItem(sessionKey, "1");
        setTimeout(() => { goalConfetti(); chime(); }, 900);
      }
    }
  }, [d]);

  if (!d) {
    return (
      <PhoneFrame>
        <div className="p-6 text-slate-400 ps-serif ps-italic">Loading your day…</div>
      </PhoneFrame>
    );
  }

  const hr = new Date().getHours();
  const greeting = hr < 5 ? "Up early," : hr < 12 ? "Good morning," : hr < 17 ? "Good afternoon," : hr < 21 ? "Good evening," : "Late night,";

  return (
    <PhoneFrame>
      <div className="min-h-screen relative isolate overflow-hidden bg-[#050E15]">
        {/* Layered home backdrop — forest path photo starts at the greeting line
            ("Alex.") and only covers the top hero area (points ring + Posture Wellness
            Score + Human Performance Score), then fades into the dark editorial
            background for the rest of the page. */}
        <div
          aria-hidden
          className="absolute left-0 right-0 bg-cover bg-center bg-no-repeat pointer-events-none"
          style={{
            backgroundImage: "url('/bg/forest-path.jpg')",
            top: "70px",
            height: "820px",
            zIndex: 0,
            WebkitMaskImage:
              "linear-gradient(180deg, #000 0%, #000 70%, transparent 100%)",
            maskImage:
              "linear-gradient(180deg, #000 0%, #000 70%, transparent 100%)",
          }}
        />
        <div
          aria-hidden
          className="absolute left-0 right-0 pointer-events-none"
          style={{
            top: "70px",
            height: "820px",
            zIndex: 1,
            background:
              "linear-gradient(180deg, rgba(5,14,21,0.55) 0%, rgba(5,14,21,0.35) 35%, rgba(5,14,21,0.55) 75%, rgba(5,14,21,0.95) 100%)",
          }}
        />
        <div className="px-5 pt-8 pb-32 relative" style={{ zIndex: 10 }}>
          {/* Top bar — editorial */}
          <div className="flex items-start justify-between ps-fade-up">
            <div>
              <div className="text-xs text-slate-400 ps-serif ps-italic">{greeting}</div>
              <div className="ps-heading text-[1.65rem] font-extrabold tracking-tight" data-testid="home-greeting-name">
                {user?.name?.split(" ")[0]}.
              </div>
            </div>
            <div className="flex items-center gap-2">
              <MiniSpinWheel claimed={spinClaimed} onClick={() => setSpinOpen(true)} />
              <div className="px-2.5 py-1 rounded-full bg-[#15303E]/80 backdrop-blur border border-[#1B364A] flex items-center gap-1.5 text-xs font-bold" data-testid="home-points">
                <Trophy size={12} color="#4ade80" />
                <span className="ps-tabular">{d.points}</span>
              </div>
              <NotificationBell />
            </div>
          </div>

          {/* Streak fire — big and proud */}
          <div className="mt-5 flex items-center justify-center gap-3" data-testid="home-streak-row">
            <FireStreak days={d.streak} size={44} />
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400 font-bold">Current streak</div>
              <div className="ps-heading text-lg font-extrabold ps-tabular" data-testid="home-streak">
                {d.streak} <span className="text-slate-500 ps-serif ps-italic font-normal text-sm">day{d.streak === 1 ? "" : "s"}</span>
              </div>
            </div>
          </div>

          {/* Hero ring */}
          <div className="mt-8 flex flex-col items-center ps-fade-up" style={{ animationDelay: "0.15s" }}>
            <StepRing active={d.active_steps} passive={d.passive_steps} goal={d.daily_goal} />
          </div>

          {/* Posture Score — moved directly under the points ring */}
          <div className="mt-5 ps-card p-5 flex items-center justify-between gap-3 ps-fade-up overflow-hidden relative" style={{ animationDelay: "0.15s" }}>
            <div className="absolute -top-10 -left-10 w-40 h-40 rounded-full bg-[#a78bfa]/10 blur-3xl pointer-events-none" />
            <div className="relative flex-1 min-w-0">
              <div className="text-sm uppercase tracking-[0.22em] text-slate-400 font-bold">Today's wellness</div>
              <div className="ps-heading text-[1.7rem] mt-1.5 font-extrabold leading-[1.05]">
                Posture Wellness <span className="ps-serif ps-italic font-normal">Score™</span>
              </div>
              <p className="text-xs text-slate-400 mt-1.5 leading-snug">Movement + posture + consistency, woven into one number.</p>
            </div>
            <PostureGauge score={d.posture_score} size={128} />
          </div>

          {/* Human Performance Score — re-interprets Apple/Google Health into the 5 pillars */}
          <PostureWellnessHero />

          {/* Annual goal */}
          <div className="mt-5 ps-fade-up" style={{ animationDelay: "0.22s" }}>
            <AnnualGoalCard />
          </div>

          {/* Daily goal progress */}
          <DailyGoalCard total={d.total_posture_steps} goal={d.daily_goal} />

          {/* Active / Passive split */}
          <div className="mt-5 grid grid-cols-2 gap-3 ps-stagger">
            <div className="ps-card p-4 relative overflow-hidden">
              <div className="absolute -bottom-6 -right-6 w-24 h-24 rounded-full bg-[#4ade80]/15 blur-2xl" />
              <div className="relative flex items-center gap-2 text-[#4ade80] text-[10px] font-bold uppercase tracking-[0.2em]">
                <Footprints size={12} /> Active
              </div>
              <div className="relative ps-heading text-3xl font-extrabold mt-1 ps-tabular" data-testid="home-active-steps">{d.active_steps.toLocaleString()}</div>
              <div className="relative text-[10px] text-slate-500 ps-serif ps-italic">posture steps</div>
            </div>
            <div className="ps-card p-4 relative overflow-hidden">
              <div className="absolute -bottom-6 -right-6 w-24 h-24 rounded-full bg-[#22d3ee]/15 blur-2xl" />
              <div className="relative flex items-center gap-2 text-[#22d3ee] text-[10px] font-bold uppercase tracking-[0.2em]">
                <Coffee size={12} /> Passive
              </div>
              <div className="relative ps-heading text-3xl font-extrabold mt-1 ps-tabular" data-testid="home-passive-steps">{d.passive_steps.toLocaleString()}</div>
              <div className="relative text-[10px] text-slate-500 ps-serif ps-italic">@ 50% credit</div>
            </div>
          </div>

          {/* AI Coach — surfaces today's proactive nudge if available */}
          <CoachNudgeCard fallback={d.checkin_today
            ? `Nice — your check-in today shows momentum. ${d.daily_goal - d.total_posture_steps > 0 ? `Just ${(d.daily_goal - d.total_posture_steps).toLocaleString()} steps to goal.` : `You've crushed today's goal.`}`
            : `Start with today's check-in. It takes 30 seconds and sharpens your score.`} />

          {/* Quick actions — gamified */}
          <div className="mt-7">
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400 font-bold mb-3 flex items-center justify-between">
              <span>Quick <span className="ps-serif ps-italic normal-case text-slate-500">actions</span></span>
              <span className="text-[#4ade80] normal-case ps-serif ps-italic font-normal">tap to earn</span>
            </div>

            {/* Hero — Start Walk */}
            <QuickActionCard
              to="/walk"
              testId="qa-walk"
              variant="hero"
              accent="#4ade80"
              icon={Footprints}
              tagline="Today's movement"
              title="Start Walk"
              primaryValue={`${d.active_steps.toLocaleString()} / ${d.daily_goal.toLocaleString()}`}
              primaryUnit="steps"
              secondary={`${d.calories_today || 0} cal burned · ${d.streak}-day streak`}
              reward={`Earn +50 pts`}
              rewardIcon={Star}
              progress={d.active_steps / Math.max(1, d.daily_goal)}
              showRing
              ctaLabel="Start"
            />

            {/* Secondary trio */}
            <div className="mt-3 grid grid-cols-1 gap-3 ps-stagger">
              <QuickActionCard
                to="/passive"
                testId="qa-passive"
                accent="#22d3ee"
                icon={Coffee}
                tagline="Sit tall, earn"
                title="Passive Session"
                primaryValue={d.passive_steps.toLocaleString()}
                primaryUnit="points"
                secondary={`${Math.floor((d.passive_minutes_today || 0) / 60)}h ${(d.passive_minutes_today || 0) % 60}m logged today`}
                reward="50% credit"
                rewardIcon={Zap}
                ctaLabel="Begin"
              />

              <QuickActionCard
                to="/training"
                testId="qa-training"
                accent="#FBBF24"
                icon={Activity}
                tagline="Build strength"
                title="Posture Session"
                primaryValue={`${d.posture_minutes_today || 0}`}
                primaryUnit={`/ ${d.posture_minutes_goal || 20} min`}
                secondary={(d.posture_minutes_today || 0) >= (d.posture_minutes_goal || 20) ? "Goal complete — bonus zone." : `${Math.max(0, (d.posture_minutes_goal || 20) - (d.posture_minutes_today || 0))} min to go`}
                progress={(d.posture_minutes_today || 0) / Math.max(1, d.posture_minutes_goal || 20)}
                reward="+30 pts"
                rewardIcon={Award}
                ctaLabel="Train"
              />

              <QuickActionCard
                to="/checkin"
                testId="qa-checkin"
                accent="#a78bfa"
                icon={ClipboardList}
                tagline={d.checkin_today ? "Today's reflection" : "30-second ritual"}
                title="Daily Check-In"
                primaryValue={d.checkin_today ? (MOOD_LABELS[d.latest_mood] || "Logged") : "Pending"}
                primaryUnit={d.checkin_today ? "mood" : ""}
                secondary={`Posture Score™ ${d.posture_score}/100`}
                reward={d.checkin_today ? "Done · +20 today" : "+20 pts"}
                rewardIcon={Smile}
                ctaLabel={d.checkin_today ? "Update" : "Check in"}
              />
            </div>
          </div>

          {/* Journey */}
          <Link to="/journey" className="block mt-6 ps-fade-up" data-testid="journey-card">
            <div className="ps-card p-5 relative overflow-hidden ps-topo">
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#4ade80]/15 blur-3xl" />
              <div className="relative flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#4ade80] font-bold">365-day journey</div>
                  <div className="ps-heading text-xl font-extrabold mt-1">
                    Stage 1 · <span className="ps-serif ps-italic font-normal">Build Awareness</span>
                  </div>
                </div>
                <Sparkles size={18} color="#4ade80" />
              </div>
              <div className="relative mt-4 h-2 rounded-full bg-[#15303E] overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-[#4ade80] via-[#22d3ee] to-[#a78bfa] transition-all duration-700" style={{ width: `${Math.min(100, (d.streak / 365) * 100).toFixed(1)}%` }} />
              </div>
              <div className="relative text-xs text-slate-400 mt-2 ps-serif ps-italic">Day {d.streak} of 365</div>
            </div>
          </Link>

          {/* Daily Mission — Coach-issued challenge (second last) */}
          <div className="mt-6 ps-fade-up" data-testid="home-daily-mission">
            <DailyMissionCard onClaim={() => { refreshUser?.(); api.get("/dashboard").then((r) => setD(r.data)); }} />
          </div>

          {/* Discovery tiles — 2x2 premium grid */}
          <div className="mt-6 grid grid-cols-2 gap-3 ps-stagger">
            <DiscoveryTile
              to="/leaderboard"
              icon={Trophy}
              label="Leaderboard"
              tagline="Climb the ranks"
              hint="Friends + Global"
              color="#FBBF24"
              tid="home-leaderboard"
            />
            <DiscoveryTile
              to="/duels"
              icon={Swords}
              label="Duels"
              tagline="1v1 step battles"
              hint="Stake & win"
              color="#ef4444"
              tid="home-duels"
              pulse
            />
            <DiscoveryTile
              to="/wellness"
              icon={HeartPulse}
              label="Wellness"
              tagline="Posture-translated"
              hint="5 pillars + Age"
              color="#4ade80"
              tid="home-wellness"
            />
            <DiscoveryTile
              to="/community"
              icon={Sparkles}
              label="Community"
              tagline="Posture Circles™"
              hint="Find your tribe"
              color="#22d3ee"
              tid="home-community"
            />
          </div>

          {user?.employer_code && (
            <Link to="/corporate" data-testid="home-corporate" className="mt-4 block ps-card p-4 flex items-center justify-between text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400 font-bold">Corporate Wellness</div>
                <div className="font-semibold ps-heading">{user.employer_code}</div>
              </div>
              <ChevronRight size={16} className="text-slate-500" />
            </Link>
          )}

          {user?.role === "admin" && (
            <Link to="/admin" data-testid="home-admin" className="mt-3 block ps-card p-4 text-center text-sm text-[#4ade80] font-bold">
              Admin Console →
            </Link>
          )}

          <button onClick={logout} data-testid="logout-btn" className="mt-8 text-xs text-slate-500 underline w-full ps-serif ps-italic">Log out</button>
        </div>
      </div>
      <BottomNav />
      <SpinWheel
        open={spinOpen}
        onClose={() => setSpinOpen(false)}
        onAward={() => { setSpinClaimed(true); refreshUser?.(); api.get("/dashboard").then((r) => setD(r.data)); }}
      />
    </PhoneFrame>
  );
}

function DiscoveryTile({ to, icon: Icon, label, tagline, hint, color, tid, pulse = false }) {
  return (
    <Link
      to={to}
      data-testid={tid}
      className="ps-glass-card relative overflow-hidden group block p-4 hover:-translate-y-0.5 transition-transform"
      style={{
        boxShadow: `0 1px 0 0 rgba(255,255,255,0.08) inset, 0 -1px 0 0 rgba(0,0,0,0.25) inset, 0 22px 50px -24px ${color}55`,
      }}
    >
      {/* Ambient mesh */}
      <div
        aria-hidden
        className="ps-mesh-drift absolute inset-[-30%] opacity-50 pointer-events-none"
        style={{
          background: `radial-gradient(circle at 30% 20%, ${color}45 0%, transparent 45%), radial-gradient(circle at 80% 80%, ${color}25 0%, transparent 50%)`,
          filter: "blur(18px)",
        }}
      />
      {/* Gradient border */}
      <div
        aria-hidden
        className="absolute inset-0 rounded-3xl pointer-events-none opacity-60"
        style={{
          background: `linear-gradient(135deg, ${color}66 0%, transparent 40%, transparent 60%, ${color}33 100%)`,
          padding: 1,
          WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
          WebkitMaskComposite: "xor",
          maskComposite: "exclude",
        }}
      />
      {/* Diagonal shimmer */}
      <div aria-hidden className="absolute inset-0 overflow-hidden pointer-events-none rounded-3xl">
        <div
          className="ps-shimmer-x absolute -inset-y-4 w-1/3"
          style={{
            background: `linear-gradient(100deg, transparent 0%, ${color}25 50%, transparent 100%)`,
            transform: "translateX(-120%)",
          }}
        />
      </div>

      {/* Embossed pressable icon */}
      <div
        className="relative w-14 h-14 rounded-2xl flex items-center justify-center transition-transform duration-200 group-hover:scale-105 group-active:scale-95"
        style={{
          background: `linear-gradient(160deg, ${color}55 0%, ${color}22 40%, ${color}10 100%)`,
          border: `1px solid ${color}66`,
          boxShadow:
            `inset 0 1.5px 0 rgba(255,255,255,0.32),` +
            `inset 0 -3px 6px rgba(0,0,0,0.45),` +
            `inset 0 0 16px ${color}26,` +
            `0 6px 18px -6px ${color}88,` +
            `0 2px 0 rgba(0,0,0,0.4)`,
        }}
      >
        <span
          aria-hidden
          className="absolute inset-x-2 top-1 h-1/3 rounded-t-xl pointer-events-none"
          style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.22), transparent)" }}
        />
        <Icon
          size={26}
          color={color}
          strokeWidth={2.4}
          style={{ filter: `drop-shadow(0 2px 4px ${color}55) drop-shadow(0 0 10px ${color}55)` }}
        />
        {pulse && (
          <span
            aria-hidden
            className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full animate-pulse"
            style={{ background: color, boxShadow: `0 0 10px ${color}` }}
          />
        )}
      </div>

      {/* Text */}
      <div className="relative mt-3">
        <div className="ps-heading font-extrabold text-[15px] leading-tight">{label}</div>
        {tagline && (
          <div className="text-[11px] ps-serif ps-italic text-slate-400 mt-0.5 leading-snug">
            {tagline}
          </div>
        )}
      </div>

      {/* Hint pill */}
      <div className="relative mt-3 flex items-center justify-between">
        {hint && (
          <span
            className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest"
            style={{
              background: `${color}1f`,
              color: color,
              border: `1px solid ${color}44`,
            }}
          >
            {hint}
          </span>
        )}
        <ChevronRight size={14} color={color} className="opacity-70 group-hover:translate-x-0.5 transition-transform" />
      </div>
    </Link>
  );
}

function DailyGoalCard({ total, goal }) {
  const pct = Math.min(100, (total / Math.max(1, goal)) * 100);
  const remaining = Math.max(0, goal - total);
  const hit = total >= goal;
  return (
    <div className="mt-6 ps-card p-4 flex items-center gap-4 relative overflow-hidden" data-testid="daily-goal-card">
      <div className="absolute -bottom-8 -right-8 w-28 h-28 rounded-full bg-[#4ade80]/15 blur-3xl pointer-events-none" />
      <div className="relative">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#4ade80] font-bold">Daily goal</div>
        <div className="ps-heading text-xl font-extrabold ps-tabular mt-0.5">
          {goal.toLocaleString()}
          <span className="text-xs text-slate-500 ml-1 ps-serif ps-italic font-normal">steps</span>
        </div>
        <div className="text-[10px] text-slate-400 ps-serif ps-italic mt-0.5" data-testid="daily-goal-remaining">
          {hit ? "Crushed it for today." : `${remaining.toLocaleString()} to go`}
        </div>
      </div>
      <div className="relative flex-1 flex flex-col items-end">
        <div className="ps-heading text-2xl font-extrabold ps-gradient-text ps-tabular" data-testid="daily-goal-pct">
          {Math.round(pct)}%
        </div>
        <div className="w-full mt-2 h-1.5 rounded-full bg-[#0E1F2A] overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-[#4ade80] to-[#22d3ee] transition-all duration-700"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    </div>
  );
}
